/**
 * Compression d'image côté client (avant upload).
 *
 * Pas de dépendance native serveur (sharp) : on redimensionne et ré-encode
 * l'image dans un canvas. Les PDF et fichiers non-image sont renvoyés tels quels.
 * Utilisé pour les documents d'urbanisme (PLU scannés, zonages…) et limite la
 * taille stockée sur R2.
 */

const MAX_DIMENSION = 2400; // px (côté le plus long)
const JPEG_QUALITY = 0.82;

/**
 * Compresse une image si c'est un type raster compressible (jpeg/png/webp).
 * Renvoie le fichier d'origine pour les PDF ou si la compression échoue / n'aide pas.
 */
export async function compressImageIfNeeded(file: File): Promise<File> {
  const compressible = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  if (!compressible) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close(); return file; }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob) return file;
    // Ne garde la version compressée que si elle est réellement plus légère.
    if (blob.size >= file.size) return file;

    const newName = file.name.replace(/\.(png|webp|jpe?g)$/i, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch {
    return file; // en cas d'échec, on upload l'original
  }
}

// ── Preset « photo de fiche » (matériel / salles / véhicules) ───────────────
// Item 3.10 : 2 photos max par fiche, ULTRA-COMPRESSÉES. Ces photos servent à
// reconnaître un bien (une vue de face, une de côté), pas à lire un document :
// 1280 px et qualité 0.7 suffisent largement et font tomber une photo de
// téléphone de ~4 Mo à ~150-250 Ko. On ne touche pas au preset document
// ci-dessus (PLU scannés, cartes grises OCR) qui a besoin de sa définition.
const PHOTO_MAX_DIMENSION = 1280; // px (côté le plus long)
const PHOTO_JPEG_QUALITY = 0.7;

/**
 * Compresse agressivement une photo de fiche (matériel, salle, véhicule).
 * Renvoie le fichier d'origine si ce n'est pas une image compressible ou si la
 * compression échoue / n'allège pas.
 */
export async function compressPhotoFiche(file: File): Promise<File> {
  const compressible = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  if (!compressible) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, PHOTO_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) { bitmap.close(); return file; }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', PHOTO_JPEG_QUALITY)
    );
    if (!blob) return file;
    if (blob.size >= file.size) return file;

    const newName = file.name.replace(/\.(png|webp|jpe?g)$/i, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
